import { useEffect, useRef } from 'react';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import { completeRecording } from '../model/transcriptorSlice';
import { selectConsultationById } from '../model/transcriptorSlice.selectors';

import { useEncounterPolling } from './useEncounterPolling';

type UseRecordingCompletionOptions = {
  consultationId: string | undefined;
  patientId: string | undefined;
  isProcessing: boolean;
  onComplete: (consultationId: string) => void;
};

// The processing of a v2 recording ends with its stored encounter, which the
// encounter's one poller keeps current for every view that follows it.
export const useRecordingCompletion = ({
  consultationId,
  patientId,
  isProcessing,
  onComplete,
}: UseRecordingCompletionOptions) => {
  const dispatch = useAppDispatch();
  const status = useAppSelector((state) =>
    consultationId
      ? selectConsultationById(state, consultationId)?.status
      : undefined,
  );
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEncounterPolling({
    encounterId: consultationId,
    patientId,
    active: isProcessing,
  });

  useEffect(() => {
    if (
      !consultationId ||
      !isProcessing ||
      (status !== 'succeeded' && status !== 'failed')
    ) {
      return;
    }
    dispatch(completeRecording());
    onCompleteRef.current(consultationId);
  }, [consultationId, isProcessing, status]);
};
